import { enableProdMode } from '@angular/core';
import { environment } from '@env/environment';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideCore } from '@portal-output/core/core.providers';
import { routes } from '@portal-output/app/app.routes';
import { AppComponent } from '@portal-output/app/components/app/app.component';
import { provideStore, provideStates } from '@ngxs/store';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideAuthServices } from '@portal-core/auth/services/auth-services.providers';
import { provideCurrentServices } from '@portal-core/current/services/current-services.providers';
import { AuthLogoutRedirectEffect } from '@portal-core/auth/effects/auth-logout-redirect.effect';
import { AuthResettablesEffect } from '@portal-core/auth/effects/auth-resettables.effect';
import { addIdAddTypeToPageFilterGroup } from '@portal-core/ui/page-filters/migrations/add.id.add.type.to.page-filter-group.migration';
import { CoreState } from '@portal-output/core/services/core-data.service';
import { AuthEventHandlerService } from '@portal-output/core/auth/auth-event-handler.service';
import { CurrentConfiguratorService } from '@portal-output/core/current/current-configurator.service';
import { BuildsState } from '@portal-core/builds/services/builds-data.service';
import { ContentSecurityPolicyState } from '@portal-core/sites/services/content-security-policy/content-security-policy-data.service';
import { CurrentUserPermissionsState } from '@portal-core/permissions/services/current-user-permissions-data.service';
import { LicenseUsersState } from '@portal-core/license-users/services/license-users-data.service';
import { LicensesState } from '@portal-core/licenses/services/licenses-data.service';
import { MessagesState } from '@portal-core/messages/services/messages-data.service';
import { NotificationsState } from '@portal-core/notifications/services/notifications-data.service';
import { ProjectChecklistItemsState } from '@portal-core/project-checklists/services/project-checklist-items/project-checklist-items-data.service';
import { ProjectChecklistsState } from '@portal-core/project-checklists/services/project-checklists/project-checklists-data.service';
import { ProjectsState } from '@portal-core/projects/services/projects-data.service';
import { ReviewFileUsersState } from '@portal-core/reviews/review-file-users/services/review-file-users-data.service';
import { ReviewFilesState } from '@portal-core/reviews/review-files/services/review-files-data.service';
import { ReviewPackageUsersState } from '@portal-core/reviews/review-package-users/services/review-package-users-data.service';
import { ReviewPackagesState } from '@portal-core/reviews/review-packages/services/review-packages-data.service';
import { RobotsExclusionState } from '@portal-core/sites/services/robots-exclusion/robots-exclusion-data.service';
import { SiteStylesState } from '@portal-core/sites/services/site-styles/site-styles-data.service';
import { SitesState } from '@portal-core/sites/services/sites-data.service';
import { TaskAssetsState } from '@portal-core/tasks/services/task-assets-data.service';
import { TaskCommentsState } from '@portal-core/tasks/services/task-comments-data.service';
import { TasksState } from '@portal-core/tasks/services/tasks-data.service';
import { TeamsState } from '@portal-core/teams/services/teams-data.service';
import { UsersState } from '@portal-core/users/services/users-data.service';
import { WebhookChannelsState } from '@portal-core/slack/services/slack-webhooks-channels-data.service';
import { WebhookSubscribersState } from '@portal-core/slack/services/slack-webhooks-subscribers-data.service';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    ...provideCore(),
    provideRouter(routes),
    provideStore(
      [CoreState],
      withNgxsStoragePlugin({
        keys: [
          'coreState.licenseId'
        ],
        afterDeserialize: (state, key) => {
          return addIdAddTypeToPageFilterGroup(state, key);
        }
      }),
      withNgxsReduxDevtoolsPlugin({
        disabled: environment.production
      })
    ),
    provideStates([
      BuildsState,
      ContentSecurityPolicyState,
      CurrentUserPermissionsState,
      LicenseUsersState,
      LicensesState,
      MessagesState,
      NotificationsState,
      ProjectChecklistItemsState,
      ProjectChecklistsState,
      ProjectsState,
      ReviewFileUsersState,
      ReviewFilesState,
      ReviewPackageUsersState,
      ReviewPackagesState,
      RobotsExclusionState,
      SiteStylesState,
      SitesState,
      TaskAssetsState,
      TaskCommentsState,
      TasksState,
      TeamsState,
      UsersState,
      WebhookChannelsState,
      WebhookSubscribersState,
    ]),
    provideAuthServices({
      effects: [AuthResettablesEffect, AuthLogoutRedirectEffect],
      onAuthError: [AuthEventHandlerService],
      onAuthRoute: AuthEventHandlerService
    }),
    provideCurrentServices({
      getLicenseUser: CurrentConfiguratorService,
      getLicenseUser$: CurrentConfiguratorService,
      getLicense: CurrentConfiguratorService,
      getLicense$: CurrentConfiguratorService,
      getActiveLicense: CurrentConfiguratorService,
      getActiveLicense$: CurrentConfiguratorService,
      isSysAdmin: CurrentConfiguratorService
    })
  ]
})
  .catch(err => console.error(err));
